// @flow
import { IS_SYNC } from "../../constants/Constants";
import { cacheContributions } from "../../constants";

import serverFetch from "./serverFetch";
import xhr from "./xhr";
import Cache from "../browser/Cache";

import type { RequestOptions } from "./types";

type CachedResponse = Object | Array<any> | string;

const IS_FETCHING = "is-fetching";

/**
 * Checks each x ms if a cache item is set after fetching the item.
 * @private
 */
const waitForCachedItem = (cacheKey: string): Promise<CachedResponse> => {
  let TIMEOUT = 3000;
  const CHECK_TIMEOUT = 100;

  return new Promise<CachedResponse>((resolve, reject) => {
    const check = () => {
      if (Cache.getItem(cacheKey) !== IS_FETCHING) {
        const cachedItem = Cache.getItem(cacheKey);
        if (cachedItem != null) {
          resolve(cachedItem);
        } else {
          reject(new Error("Empty cached item"));
        }
      } else if (TIMEOUT <= 0) {
        reject(new Error("Empty cached item"));
      } else {
        TIMEOUT = TIMEOUT - CHECK_TIMEOUT;
        setTimeout(check, CHECK_TIMEOUT);
      }
    };

    setTimeout(check, CHECK_TIMEOUT);
  });
};

/**
 * @private
 */
const browserFetch = (args: RequestOptions): Promise<CachedResponse> => {
  const shouldCache = cacheContributions() && args.cache;

  if (shouldCache) {
    const cacheKey = Cache.createResourceKey(args);

    if (Cache.hasItem(cacheKey)) {
      const cacheItem = Cache.getItem(cacheKey);

      if (cacheItem === IS_FETCHING) {
        return waitForCachedItem(cacheKey);
      }

      if (cacheItem != null) {
        return Promise.resolve(cacheItem);
      }
    }

    Cache.addItem(cacheKey, IS_FETCHING);
  }

  return xhr(args).then((response: CachedResponse) => {
    if (shouldCache) {
      const cacheKey = Cache.createResourceKey(args);
      Cache.addItem(cacheKey, response);
    }

    return response;
  });
};

/**
 * Request data from server, uses server datafetcher or xhr in browser.
 */
const universalFetch = (
  args: RequestOptions,
): Promise<CachedResponse> | Promise<any> => {
  // Set locale as Accept-Language header
  if (args.locale) {
    // $FlowFixMe
    args.headers = {
      ...args.headers,
      "Accept-Language": args.locale,
    };
  }

  if (args.includeContext) {
    args.url = `${args.url}?includeContext=true`;
  }

  // server side no access to the browser cache apis, no cache available
  if (IS_SYNC) {
    return serverFetch(args);
  }

  return browserFetch(args);
};

export default universalFetch;
